import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import logoMark from "@/assets/logo-mark.png";

// Lote 144 — página aberta pelo link do e-mail (ou do administrador): /redefinir-senha#token=…
// O token fica no fragmento da URL, que o navegador não envia ao servidor (não aparece em logs).
const lerToken = () => {
  try { return new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") || ""; } catch { return ""; }
};

export default function RedefinirSenha() {
  const navigate = useNavigate();
  const [token] = useState(lerToken);
  const [senha, setSenha] = useState("");
  const [repetir, setRepetir] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [erro, setErro] = useState("");

  const salvar = async (e) => {
    e.preventDefault();
    setErro("");
    if (senha.length < 8) { setErro("A senha precisa ter pelo menos 8 caracteres."); return; }
    if (senha !== repetir) { setErro("As duas senhas não são iguais."); return; }
    setSubmitting(true);
    try {
      await http.post("/auth/redefinir-senha", { token, password: senha });
      toast.success("Senha criada. Entre com ela.");
      navigate("/login?tab=senha", { replace: true });
    } catch (err) {
      setErro(err?.response?.data?.detail || "Não foi possível salvar agora. Tente de novo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-16 bg-white">
      <div className="w-full max-w-sm mx-auto space-y-8">
        <div className="flex items-center gap-2">
          <img src={logoMark} alt="" className="w-9 h-9 rounded-md" />
          <span className="font-display text-xl font-bold"><span className="text-gold-600">IA</span>pauta</span>
        </div>

        {!token ? (
          <div className="space-y-4" data-testid="redefinir-sem-link">
            <h2 className="font-display text-2xl font-bold text-stone-900">Link incompleto</h2>
            <p className="text-sm text-stone-500 leading-relaxed">
              Este endereço não traz o código do link. Abra o link do e-mail inteiro ou peça um novo.
            </p>
            <Link to="/esqueci-senha" className="inline-flex items-center gap-1.5 text-sm text-emerald-brand font-medium hover:underline">
              Pedir novo link
            </Link>
          </div>
        ) : (
          <>
            <div>
              <h2 className="font-display text-3xl font-bold text-stone-900 mb-2">Criar nova senha</h2>
              <p className="text-sm text-stone-500 leading-relaxed">Escolha uma senha com pelo menos 8 caracteres.</p>
            </div>
            <form onSubmit={salvar} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="nova-senha">Nova senha</Label>
                <Input id="nova-senha" data-testid="nova-senha-input" type="password" autoComplete="new-password" required minLength={8}
                  value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="Mínimo 8 caracteres" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="repetir-senha">Repita a senha</Label>
                <Input id="repetir-senha" data-testid="repetir-senha-input" type="password" autoComplete="new-password" required
                  value={repetir} onChange={(e) => setRepetir(e.target.value)} placeholder="Digite de novo" />
              </div>
              {erro && <p data-testid="redefinir-erro" className="text-sm text-rose-600">{erro}</p>}
              <Button data-testid="redefinir-salvar" type="submit" disabled={submitting} className="w-full h-11 bg-emerald-brand text-white font-medium rounded-md">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar nova senha"}
              </Button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800">
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Voltar para o login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
